import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/stores/auth.store";
import type { OrderFormData } from "../../../shared/schemas/order.schema";
import { trpcClient } from "@/lib/trpc";
import { safeFormatDateInput } from "@/lib/utils";
import OrderForm from "./OrderForm";
import { toast } from "sonner";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { formatProductSpecs } from "@/lib/format-specs";

export default function OrderCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Read FK prefill params from URL (e.g. from CustomerView "Tạo đơn hàng" or QuotationView "Tạo Đơn hàng")
  const searchParams = useSearch({ strict: false }) as Record<string, string>;
  const prefillCustomerId = searchParams?.customerId || "";
  const prefillCustomerName = searchParams?.customerName || "";
  const prefillQuotationId = searchParams?.quotationId || "";
  const prefillPhone = searchParams?.phone || "";
  const prefillEmail = searchParams?.email || "";

  // When coming from an approved Quotation, fetch its details to pre-fill the order
  const { data: quotationData, isLoading: isLoadingQuotation } = useQuery({
    queryKey: ["quotation-for-order", prefillQuotationId],
    queryFn: async () =>
      trpcClient.quotations.getById.query({ id: prefillQuotationId }),
    enabled: !!prefillQuotationId,
  });

  const currentUser = useAuthStore((s) => s.user);

  // Build default values from quotation data (if available)
  const buildDefaultValues = (): Partial<OrderFormData> => {
    const base: Partial<OrderFormData> = {};

    // Always preset salesUserId if we have a logged-in user
    if (currentUser?.id) {
      base.salesUserId = currentUser.id;
    }

    // Customer prefill
    if (prefillCustomerId) {
      base.customerId = prefillCustomerId;
      base.customerName = prefillCustomerName;
      if (prefillPhone) base.phone = prefillPhone;
      if (prefillEmail) base.email = prefillEmail;
    }

    // Quotation prefill — map QuotationDetail → OrderDetail
    if (quotationData) {
      const q = quotationData as any;
      base.customerId = base.customerId || q.customerId;
      base.customerName = base.customerName || q.customer?.name || "";
      base.contactPerson = q.contactPerson || q.customer?.contactPerson || "";
      base.phone = q.phone || q.customer?.phone || "";
      base.email = q.email || q.customer?.email || "";
      base.deliveryAddress =
        q.deliveryAddress ||
        q.customer?.deliveryAddress ||
        q.customer?.address ||
        "";
      base.vatRate = Number(q.vatRate) || 0;
      base.shippingFee = Number(q.shippingFee) || 0;
      base.exchangeRate = Number(q.exchangeRate) || 1;
      base.currency = q.currency || "VND";
      base.paymentTerm = q.paymentTerm || q.customer?.paymentTerms || q.customer?.paymentTerm || "";
      base.deposit = q.deposit || "";
      base.deliveryMethod = q.deliveryMethod || q.customer?.deliveryMethod || "";
      base.priority = (q.priority as any) || "NORMAL";
      const today = safeFormatDateInput(new Date());
      base.orderDate = today;
      const d = new Date(today);
      d.setDate(d.getDate() + 7);
      base.deliveryDeadline = safeFormatDateInput(d);
      base.salesUserId = q.salesUserId || q.creatorId || currentUser?.id || "";
      base.notes = `Từ báo giá ${q.quotationId}`;

      // Map details
      let details = (q.details || []) as any[];
      const selectedDetailIdsStr = searchParams?.selectedDetailIds as string;
      if (selectedDetailIdsStr) {
        const arr = selectedDetailIdsStr.split(",");
        details = details.filter((d) => arr.includes(d.id));
      }

      if (details.length > 0) {
        base.orderDetails = details.map((d: any) => {
          const unitPrice =
            Number(d.quotedPrice) ||
            Number(d.priceVND) ||
            Number(d.unitPrice) ||
            0;
          const quantity = Number(d.quantity) || 0;
          const amount = unitPrice * quantity;

          // Extract prepress data from d.costEstimate or direct prepress fields
          const firstComp = d.costEstimate?.components?.[0];
          const prepressCode = d.costEstimate?.estimateCode || d.prepressCode || "";
          const prepressPaperSize = firstComp?.sheetFormat || firstComp?.prepressParams?.sheetFormat || (firstComp?.sheetWidth && firstComp?.sheetLength ? `${firstComp.sheetWidth} x ${firstComp.sheetLength} mm` : "") || d.prepressPaperSize || "";
          const prepressUps = firstComp?.outUp || firstComp?.ups || firstComp?.prepressParams?.ups || (d.prepressUps != null ? Number(d.prepressUps) : undefined);
          const prepressPrintSheets = firstComp?.grossSheets || firstComp?.materials?.[0]?.totalSheets || (d.prepressPrintSheets != null ? Number(d.prepressPrintSheets) : undefined);
          const prepressNotes = d.costEstimate?.notes || d.prepressNotes || "";

          const rawSpecs = d.specs || d.product?.specs;
          const formattedSpecs =
            typeof rawSpecs === "object" && rawSpecs
              ? formatProductSpecs(rawSpecs, d.product || d)
              : typeof rawSpecs === "string" && rawSpecs.trim().startsWith("{")
                ? formatProductSpecs(rawSpecs, d.product || d)
                : rawSpecs || d.product?.packingSpec || "";

          const priceVND = Number(d.priceVND) || (base.currency === "VND" ? unitPrice : (base.exchangeRate ? unitPrice * base.exchangeRate : unitPrice));
          const priceUSD = d.priceUSD != null && Number(d.priceUSD) > 0
            ? Number(d.priceUSD)
            : (base.currency === "USD" ? unitPrice : (base.exchangeRate && base.exchangeRate > 1 ? Number((priceVND / base.exchangeRate).toFixed(4)) : undefined));
          const costPrice = Number(d.costPrice || d.product?.costPrice || 0);
          const profitMargin = Number(d.profitMargin || 0);
          const allowancePercent = Number(d.allowancePercent || q.quantityTolerance || q.customer?.tolerancePercent || 0);

          return {
            productId: d.productId || d.product?.id || "",
            productCode: d.productCode || d.product?.productCode || "",
            productName: d.productName || d.product?.productName || "",
            specs: formattedSpecs,
            process:
              typeof d.process === "object"
                ? JSON.stringify(d.process)
                : d.process || d.product?.process || "",
            unit: d.unit || d.product?.unit || "tờ",
            quantity,
            unitPrice,
            priceVND,
            priceUSD,
            costPrice,
            profitMargin,
            allowancePercent,
            amount,
            notes: d.notes || "",
            poReference: "",
            deliveryDeadline: undefined, // Smart Sync: Lấy từ hạn giao hàng của đơn hàng tổng (xử lý ở form)
            quotationDetailId: d.id || "",
            prepressCode,
            prepressPaperSize,
            prepressUps,
            prepressPrintSheets,
            prepressNotes,
            remainingPlanQty: quantity,
          };
        });
      }
    }

    return base;
  };

  const onSubmit = async (data: OrderFormData) => {
    try {
      console.warn("--- HANDLE SUBMIT TRIGGERED ---", data);

      // Format orderDate to start of day (midnight UTC) to avoid timezone shifts
      let parsedOrderDate = data.orderDate
        ? new Date(data.orderDate)
        : new Date();
      // Backend tự tính subtotal, vatAmount, totalAmount từ orderDetails
      const created = await trpcClient.orders.create.mutate({
        orderId: data.orderId,

        customerId: data.customerId,
        customerName: data.customerName || null,
        contactPerson: data.contactPerson || null,
        phone: data.phone || null,
        email: data.email || null,
        updateCustomerInfo: data.updateCustomerInfo ?? false,
        orderDate: data.orderDate,
        deliveryDeadline: data.deliveryDeadline || null,
        deliveryAddress: data.deliveryAddress || null,
        vatRate: data.vatRate,
        shippingFee: data.shippingFee,
        currency: data.currency,
        exchangeRate: data.exchangeRate,
        deposit: data.deposit || null,
        paymentTerm: data.paymentTerm || null,
        quotationId: prefillQuotationId || null,
        status: data.status,
        saveMode: (data as any).saveMode,
        orderType: (data as any).orderType || "PRODUCTION",
        sampleType: (data as any).sampleType || null,
        notes: data.notes || null,
        salesUserId: data.salesUserId || null,
        version: 1,
        approvedDate: null,
        orderDetails: data.orderDetails?.map((d: any) => ({
          productName: d.productName,
          productCode: d.productCode || null,
          productId: d.productId || null,
          specs: d.specs || null,
          unit: d.unit || null,
          process: (d as any).process || null,
          quantity: d.quantity,
          unitPrice: d.unitPrice,
          priceVND: d.priceVND ?? d.unitPrice,
          priceUSD: d.priceUSD ?? null,
          costPrice: d.costPrice ?? 0,
          amount: (d.quantity || 0) * (d.unitPrice || 0),
          allowancePercent: d.allowancePercent ?? undefined,
          stockQty: d.stockQty ?? 0,
          lsxQty: d.lsxQty ?? 0,
          poReference: d.poReference || null,
          soReference: d.soReference || null,
          deliveryDeadline: d.deliveryDeadline || null,
          quotationDetailId: d.quotationDetailId || null,
          prepressCode: d.prepressCode || null,
          prepressPaperSize: d.prepressPaperSize || null,
          prepressUps: d.prepressUps != null ? Number(d.prepressUps) : null,
          prepressPrintSheets: d.prepressPrintSheets != null ? Number(d.prepressPrintSheets) : null,
          prepressNotes: d.prepressNotes || null,
          remainingPlanQty: d.remainingPlanQty != null ? Number(d.remainingPlanQty) : d.quantity,
        })),
      } as any);

      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      if ((created as any)?.id) {
        navigate({
          to: "/orders/$orderId",
          params: { orderId: (created as any).id },
          replace: true,
        });
      } else {
        navigate({ to: "/orders", replace: true });
      }
    } catch (err: any) {
      toast.error(err?.message || "Tạo đơn hàng thất bại");
    }
  };

  // Show loading while fetching quotation data
  if (prefillQuotationId && isLoadingQuotation) {
    return (
      <StandardDetailLayout
        breadcrumbs={[
          { label: "Đơn hàng", to: "/orders" },
          { label: "Tạo mới" },
        ]}
        title="Tạo Đơn Hàng Mới"
        onBack={() => navigate({ to: "/orders" })}
        showContextPanel={false}
      >
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
          <span className="ml-3 text-slate-700">
            Đang tải dữ liệu báo giá...
          </span>
        </div>
      </StandardDetailLayout>
    );
  }

  return (
    <StandardDetailLayout
      breadcrumbs={[
        { label: "Đơn hàng", to: "/orders" },
        { label: "Tạo mới" },
      ]}
      title="Tạo Đơn Hàng Mới"
      onBack={() => navigate({ to: "/orders" })}
      showContextPanel={false}
    >
      <div className="w-full space-y-4 animate-in fade-in duration-300">
        <OrderForm
          defaultValues={buildDefaultValues()}
          onSubmit={onSubmit}
          onCancel={() => navigate({ to: "/orders" })}
          submitLabel="Lưu"
        />
      </div>
    </StandardDetailLayout>
  );
}
